import React from 'react';
import { ENTITY_TYPES, EVENTS, Lang } from 'shared/constants';
import { UploadDocument } from 'src/components/common';
import styles from './checklist-item-editor.module.scss';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MrkdwnEditor, TextArea } from 'src/components/core';
import { TagsSelector, TAG_SELECTOR_TYPE } from 'src/components/tags/tagSelector';
import { Modal } from 'src/components/design-system';
import analyticsService from 'src/helpers/analytics.service';

const CHECKLIST_ITEM_SCHEMA = Yup.object().shape({
  title: Yup.string().min(2, 'Too Short!').required('Required!'),
  link: Yup.string().url(),
  info: Yup.string(),
  specialty: Yup.string().nullable(),
  subspecialty: Yup.string().nullable(),
  contentType: Yup.string().nullable(),
  targetAudience: Yup.array().of(Yup.string()),
});

const ChecklistItemEditorComponent = React.memo(function ChecklistItemEditor({
  open = false,
  lang = Lang.CHECKLIST_ITEM_EDITOR,
  value,
  categoryId = null,
  createItem = () => {},
  updateItem = () => {},
  onFinish = () => {},
}) {
  value = value ?? {};
  const isNew = !value.id;
  const [isLoading, setLoadingState] = React.useState(false);

  const updateOrGenerateValue = React.useCallback(
    async item => {
      setLoadingState(true);

      const { specialty, subspecialty, contentType, targetAudience, ...valuesData } = item;

      const itemData = {
        item: {
          ...valuesData,
          tags: { specialty, subspecialty, contentType, targetAudience },
        },
      };

      if (categoryId) {
        itemData.categoryId = categoryId;
      }

      // TODO: analytics
      if (isNew) {
        analyticsService.track(EVENTS.CONTENT.UPLOAD, {
          ...itemData,
          entityType: ENTITY_TYPES.admissionItem,
        });
        await createItem(itemData);
      } else {
        itemData.checkId = value.id;
        analyticsService.track(EVENTS.CONTENT.UPDATE, {
          ...itemData,
          entityType: ENTITY_TYPES.admissionItem,
        });
        await updateItem(itemData);
      }

      onFinish(true);
      setLoadingState(false);
    },
    [categoryId, createItem, isNew, onFinish, updateItem, value.id],
  );

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    values,
    errors,
    touched,
    dirty,
    isValid,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: CHECKLIST_ITEM_SCHEMA,
    initialValues: {
      ...value.tags,
      ...value,
    },
    onSubmit: updateOrGenerateValue,
  });

  const dismiss = React.useCallback(() => {
    // TODO: analytics

    onFinish();
  }, [onFinish]);

  const resetField = React.useCallback(
    field => {
      // Clear the dependent field through Formik. resetForm() repointed initialValues at the
      // (mutated) current values, which dropped `dirty` and — with enableReinitialize — made the
      // next effect snap the whole form back to the entity, reverting the value the user had just
      // cleared. That made the specialty clear-X a no-op. See BB-17.
      setFieldValue(field, null);
    },
    [setFieldValue],
  );

  return (
    <Modal
      title={isNew ? lang.CREATE_NEW_TITLE : lang.UPDATE_TITLE}
      open={open}
      onCancel={dismiss}
      onSubmit={handleSubmit}
      cancelText={lang.DISMISS}
      submitText={isNew ? lang.CREATE : lang.UPDATE}
      loading={isLoading}
      submitDisabled={!dirty || !isValid || isLoading}
      width={600}
      styles={{
        body: {
          maxHeight: '75vh',
          overflowY: 'auto',
        },
      }}>
      <form onSubmit={handleSubmit} data-testid="admission-item-form">
        <label>{lang.ITEM_TITLE}</label>
        <MrkdwnEditor
          name="title"
          value={values.title}
          error={errors.title}
          touched={touched.title}
          onChange={setFieldValue}
          placeholder={lang.ITEM_TITLE_PLACEHOLDER}
        />

        <label>{lang.ITEM_INFO}</label>
        <TextArea
          inputStyle="editor"
          name="info"
          value={values.info}
          error={errors.info}
          touched={touched.info}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={lang.ITEM_INFO_PLACEHOLDER}
        />

        <TagsSelector
          selectedValues={values}
          errors={errors}
          onChange={setFieldValue}
          resetField={resetField}
          type={TAG_SELECTOR_TYPE.CHECKLIST}
        />

        <label>{lang.ITEM_REFERENCE}</label>
        <UploadDocument
          setFieldValue={setFieldValue}
          name="link"
          value={values.link}
          error={errors.link}
          touched={touched.link}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={lang.ITEM_REFERENCE_PLACEHOLDER}
        />

        {!isNew && <span className={styles.itemId}>Id: {value?.id}</span>}
      </form>
    </Modal>
  );
});

export { ChecklistItemEditorComponent };
